/**
 * SAAED FAQ Search Utility
 * 
 * This module provides semantic search functionality for the SAAED FAQ knowledge base.
 * It supports multilingual search (Arabic, English, and Urdu) and returns relevant FAQs based on user queries.
 * 
 * FAQs are now fetched from the backend API instead of static JSON import.
 */

// Cache for loaded FAQs to avoid repeated API calls
let cachedFAQs: FAQ[] | null = null;

export interface FAQ {
  id: number;
  category: string;
  question_ar: string;
  question_en: string;
  question_ur?: string;
  answer_ar: string;
  answer_en: string;
  answer_ur?: string;
  keywords: string[];
}

export interface SearchResult {
  faq: FAQ;
  relevanceScore: number;
  matchedKeywords: string[];
}

/**
 * Normalize Arabic text for better matching
 * Removes diacritics and normalizes variations of Arabic characters
 */
function normalizeArabic(text: string): string {
  return text
    .replace(/[\u064B-\u065F]/g, '') // Remove diacritics
    .replace(/[أإآ]/g, 'ا') // Normalize alef variations
    .replace(/ة/g, 'ه') // Normalize taa marbouta
    .replace(/ى/g, 'ي') // Normalize alef maksura
    .toLowerCase();
}

/**
 * Normalize Urdu text for better matching
 * Similar to Arabic but handles Urdu-specific characters
 */
function normalizeUrdu(text: string): string {
  return text
    .replace(/[\u064B-\u065F]/g, '') // Remove diacritics
    .replace(/[أإآ]/g, 'ا') // Normalize alef variations
    .replace(/ة/g, 'ه') // Normalize taa marbouta
    .replace(/ى/g, 'ي') // Normalize alef maksura
    .toLowerCase();
}

/**
 * Normalize English text for better matching
 */
function normalizeEnglish(text: string): string {
  return text.toLowerCase().trim();
}

/**
 * Extract words from text (handles both Arabic and English)
 */
function extractWords(text: string): string[] {
  // Split by spaces and filter out empty strings
  return text.split(/\s+/).filter(word => word.length > 0);
}

/**
 * Load FAQs from the backend API
 * Results are cached to avoid repeated API calls
 */
export async function loadFAQs(): Promise<FAQ[]> {
  // Return cached FAQs if available
  if (cachedFAQs) {
    return cachedFAQs;
  }

  try {
    // Use the same config as the rest of the app
    let backendUrl = '';
    if (window._env_ && window._env_.REACT_APP_BACKEND_URL) {
      backendUrl = window._env_.REACT_APP_BACKEND_URL;
    } else {
      // fallback to hardcoded value (same as runtime-config.ts)
      backendUrl = 'https://saaedi-api.softecinternational.com';
    }
    // Fetch FAQs from backend
    const response = await fetch(`${backendUrl}/api/faq/list`);
    if (!response.ok) {
      throw new Error(`Failed to fetch FAQs: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    // Cache the FAQs
    cachedFAQs = data.faqs || [];
    console.log(`[FAQ] Loaded ${cachedFAQs?.length || 0} FAQs from backend`);
    return cachedFAQs || [];
  } catch (error) {
    console.error('[FAQ] Error loading FAQs from backend:', error);
    // Return empty array if fetch fails
    return [];
  }
}

/**
 * Clear the FAQ cache (useful for testing or if FAQs are updated)
 */
export function clearFAQCache(): void {
  cachedFAQs = null;
}

/**
 * Calculate relevance score between query and FAQ
 */
function calculateRelevance(query: string, faq: FAQ): { score: number; matchedKeywords: string[] } {
  const normalizedQuery = normalizeArabic(query) + ' ' + normalizeEnglish(query) + ' ' + normalizeUrdu(query);
  const queryWords = extractWords(normalizedQuery);
  
  let score = 0;
  const matchedKeywords: string[] = [];
  
  // Check keywords (highest weight)
  faq.keywords.forEach(keyword => {
    const normalizedKeyword = normalizeArabic(keyword) + ' ' + normalizeEnglish(keyword) + ' ' + normalizeUrdu(keyword);
    queryWords.forEach(queryWord => {
      if (normalizedKeyword.includes(queryWord) || queryWord.includes(normalizedKeyword)) {
        score += 10;
        if (!matchedKeywords.includes(keyword)) {
          matchedKeywords.push(keyword);
        }
      }
    });
  });
  
  // Check question text (medium weight)
  const normalizedQuestionAr = normalizeArabic(faq.question_ar);
  const normalizedQuestionEn = normalizeEnglish(faq.question_en);
  const normalizedQuestionUr = faq.question_ur ? normalizeUrdu(faq.question_ur) : '';
  
  queryWords.forEach(queryWord => {
    if (normalizedQuestionAr.includes(queryWord) || normalizedQuestionEn.includes(queryWord) || normalizedQuestionUr.includes(queryWord)) {
      score += 5;
    }
  });
  
  // Check answer text (low weight)
  const normalizedAnswerAr = normalizeArabic(faq.answer_ar);
  const normalizedAnswerEn = normalizeEnglish(faq.answer_en);
  const normalizedAnswerUr = faq.answer_ur ? normalizeUrdu(faq.answer_ur) : '';
  
  queryWords.forEach(queryWord => {
    if (normalizedAnswerAr.includes(queryWord) || normalizedAnswerEn.includes(queryWord) || normalizedAnswerUr.includes(queryWord)) {
      score += 2;
    }
  });
  
  // Boost score for exact phrase matches
  if (normalizedQuestionAr.includes(normalizedQuery) || normalizedQuestionEn.includes(normalizedQuery) || normalizedQuestionUr.includes(normalizedQuery)) {
    score += 20;
  }
  
  return { score, matchedKeywords };
}

/**
 * Search FAQs based on user query
 * @param query - User's question or search term
 * @param limit - Maximum number of results to return (default: 3)
 * @param minScore - Minimum relevance score threshold (default: 2)
 * @returns Array of search results sorted by relevance
 */
export async function searchFAQs(query: string, limit: number = 3, minScore: number = 2): Promise<SearchResult[]> {
  // Load FAQs from backend API (uses cache if already loaded)
  const faqs = await loadFAQs();
  
  const results: SearchResult[] = [];
  
  // Calculate relevance for each FAQ
  faqs.forEach((faq) => {
    const { score, matchedKeywords } = calculateRelevance(query, faq);
    
    if (score >= minScore) {
      results.push({
        faq: faq,
        relevanceScore: score,
        matchedKeywords
      });
    }
  });
  
  // Sort by relevance score (descending)
  results.sort((a, b) => b.relevanceScore - a.relevanceScore);
  
  // Return top results
  return results.slice(0, limit);
}

/**
 * Get FAQ by ID
 */
export async function getFAQById(id: number): Promise<FAQ | undefined> {
  const faqs = await loadFAQs();
  return faqs.find(faq => faq.id === id);
}

/**
 * Get FAQs by category
 */
export async function getFAQsByCategory(category: string): Promise<FAQ[]> {
  const faqs = await loadFAQs();
  return faqs.filter(faq => faq.category === category);
}

/**
 * Get all available categories
 */
export async function getAllCategories(): Promise<string[]> {
  const faqs = await loadFAQs();
  const categories = new Set(faqs.map(faq => faq.category));
  return Array.from(categories);
}

/**
 * Format search results for AI response (multilingual)
 */
export function formatSearchResults(results: SearchResult[], language: 'ar' | 'en' | 'ur' = 'ar'): string {
  if (results.length === 0) {
    return language === 'ar' 
      ? 'لم يتم العثور على نتائج مطابقة في قاعدة المعرفة.'
      : language === 'ur'
      ? 'علم کی بنیاد میں کوئی مماثل نتائج نہیں ملے۔'
      : 'No matching results found in knowledge base.';
  }
  
  const header = language === 'ar'
    ? `وجدت ${results.length} نتيجة ذات صلة:\n\n`
    : language === 'ur'
    ? `${results.length} متعلقہ نتائج ملے:\n\n`
    : `Found ${results.length} relevant result(s):\n\n`;
  
  const formattedResults = results.map((result, index) => {
    const question = language === 'ar' 
      ? result.faq.question_ar 
      : language === 'ur' 
      ? (result.faq.question_ur || result.faq.question_en)
      : result.faq.question_en;
      
    const answer = language === 'ar' 
      ? result.faq.answer_ar 
      : language === 'ur'
      ? (result.faq.answer_ur || result.faq.answer_en)
      : result.faq.answer_en;
    
    return `${index + 1}. ${question}\n${answer}\n`;
  }).join('\n');
  
  return header + formattedResults;
}

/**
 * Get random FAQs (useful for suggestions)
 */
export async function getRandomFAQs(count: number = 3): Promise<FAQ[]> {
  const faqs = await loadFAQs();
  const shuffled = [...faqs].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, count);
}

